import type { ProductConfig } from '@/types/product';
import type { Question, ScoringCategory } from '@/types';

// Scores mínimos de 7 pts garantem min total ≥ 70% do maxTotal

const EXPANSAO_QUESTIONS: Question[] = [

  // ============================
  // ESTRUTURA DA REDE (10 pts via C | A e B são coleta de dados)
  // ============================
  {
    id: 'exp_est_a',
    category: 'estrutura_rede',
    text: 'Qual é o nome da marca da sua franquia?',
    type: 'text',
    weight: 0,
    maxScore: 0,
    required: true
  },
  {
    id: 'exp_est_b',
    category: 'estrutura_rede',
    text: 'Qual é o site oficial da sua franquia?',
    type: 'text',
    weight: 0,
    maxScore: 0,
    required: true
  },
  {
    id: 'exp_est_c',
    category: 'estrutura_rede',
    text: 'Quantas unidades franqueadas sua rede possui atualmente?',
    type: 'single',
    options: [
      { value: 'mais_20', label: 'Mais de 20 unidades', score: 10 },
      { value: '10_a_20', label: 'Entre 10 e 20 unidades', score: 9 },
      { value: '3_a_9', label: 'Entre 3 e 9 unidades', score: 8 },
      { value: '1_a_2', label: '1 ou 2 unidades', score: 7 }
    ],
    weight: 10,
    maxScore: 10,
    required: true
  },

  // ============================
  // CAPACIDADE DE INVESTIMENTO EM EXPANSÃO (20 pts)
  // ============================
  {
    id: 'exp_inv_d',
    category: 'capacidade_investimento_expansao',
    // Acima de R$5k/mês = nota melhor (10 pts); abaixo tem penalização leve
    text: 'Qual é o seu budget mensal destinado a tráfego pago para captação de franqueados?',
    type: 'single',
    options: [
      { value: 'acima_10k', label: 'Acima de R$ 10.000/mês', score: 10 },
      { value: '5k_10k', label: 'Entre R$ 5.000 e R$ 10.000/mês', score: 10 },
      { value: '2k_5k', label: 'Entre R$ 2.000 e R$ 5.000/mês', score: 8 },
      { value: 'abaixo_2k', label: 'Abaixo de R$ 2.000/mês', score: 7 }
    ],
    weight: 10,
    maxScore: 10,
    required: true
  },
  {
    id: 'exp_inv_e',
    category: 'capacidade_investimento_expansao',
    text: 'Qual é o seu orçamento total de marketing por mês (incluindo tráfego, agência e materiais)?',
    type: 'currency',
    weight: 0,
    maxScore: 0,
    required: true
  },
  {
    id: 'exp_inv_f',
    category: 'capacidade_investimento_expansao',
    text: 'Qual é o valor da taxa de franquia (investimento inicial) cobrado do franqueado?',
    type: 'single',
    options: [
      { value: 'acima_80k', label: 'Acima de R$ 80.000', score: 10 },
      { value: '40k_80k', label: 'Entre R$ 40.000 e R$ 80.000', score: 9 },
      { value: '20k_40k', label: 'Entre R$ 20.000 e R$ 40.000', score: 8 },
      { value: 'abaixo_20k', label: 'Abaixo de R$ 20.000', score: 7 }
    ],
    weight: 10,
    maxScore: 10,
    required: true
  },

  // ============================
  // PRONTIDÃO PARA EXPANSÃO (40 pts — inclui equipe)
  // ============================
  {
    id: 'exp_pron_g',
    // "começar agora" = melhor nota; abaixo tem pontuação mais alta
    category: 'prontidao_expansao',
    text: 'Em quanto tempo você deseja iniciar a expansão ativa da sua rede?',
    type: 'single',
    options: [
      { value: 'imediato', label: 'Imediatamente', score: 10 },
      { value: 'ate_3_meses', label: 'Em até 3 meses', score: 10 },
      { value: '3_a_6_meses', label: 'Entre 3 e 6 meses', score: 8 },
      { value: 'mais_6_meses', label: 'Mais de 6 meses', score: 7 }
    ],
    weight: 10,
    maxScore: 10,
    required: true
  },
  {
    id: 'exp_pron_h',
    category: 'prontidao_expansao',
    text: 'Seu processo de vendas de franquias está estruturado (material de apresentação, COF, contrato)?',
    type: 'single',
    options: [
      { value: 'sim_completo', label: 'Sim, totalmente estruturado', score: 10 },
      { value: 'parcial', label: 'Parcialmente estruturado', score: 8 },
      { value: 'nao', label: 'Ainda não está estruturado', score: 7 }
    ],
    weight: 10,
    maxScore: 10,
    required: true
  },
  {
    id: 'exp_pron_i',
    category: 'prontidao_expansao',
    text: 'Você recebe leads interessados em comprar sua franquia de forma recorrente?',
    type: 'single',
    options: [
      { value: 'sim_muitos', label: 'Sim, recebo muitos leads regularmente', score: 10 },
      { value: 'sim_poucos', label: 'Sim, mas poucos e de forma irregular', score: 9 },
      { value: 'raramente', label: 'Raramente recebo leads', score: 8 },
      { value: 'nao', label: 'Não recebo leads no momento', score: 7 }
    ],
    weight: 10,
    maxScore: 10,
    required: true
  },
  {
    id: 'exp_eq_j',
    category: 'prontidao_expansao',
    text: 'Você possui equipe interna ou deseja contratar um time comercial para vender franquias?',
    type: 'single',
    options: [
      { value: 'time_interno', label: 'Tenho time interno próprio', score: 10 },
      { value: 'alocado_inova', label: 'Quero um time alocado pela Inova Franquias', score: 10 },
      { value: 'ainda_nao', label: 'Ainda não tenho equipe comercial', score: 7 }
    ],
    weight: 10,
    maxScore: 10,
    required: true
  }
];

// maxTotal = 10 (estrutura via C) + 20 (cap.investimento) + 40 (prontidao: g+h+i+j)
// = 70 pts. Score mínimo = 7×7 = 49 pts = 70%. Dificilmente abaixo de 70%.
const EXPANSAO_MAX_TOTAL = 70;

const questionCategories = Object.fromEntries(
  EXPANSAO_QUESTIONS.map(q => [q.id, q.category as ScoringCategory])
) as Record<string, ScoringCategory>;

export const expansaoConfig: ProductConfig = {
  id: 'expansao',
  title: 'Expansão de Franquias',
  description: 'Avalie o potencial de expansão da sua rede de franquias e descubra como a Inova Franquias pode acelerar seus resultados.',
  funilId: 66,
  crmOrigem: 'Diagnóstico Expansão',
  questions: EXPANSAO_QUESTIONS,
  pdf: 'expansao',
  companyNameQuestionId: 'exp_est_a',
  scoring: {
    categories: [
      'estrutura_rede',
      'capacidade_investimento_expansao',
      'prontidao_expansao'
    ],
    weights: {
      estrutura_rede: 10,
      capacidade_investimento_expansao: 20,
      prontidao_expansao: 40
    },
    labels: {
      estrutura_rede: 'Estrutura da Rede',
      capacidade_investimento_expansao: 'Capacidade de Investimento',
      prontidao_expansao: 'Prontidão para Expansão'
    },
    maxTotal: EXPANSAO_MAX_TOTAL,
    improvements: {
      estrutura_rede: 1.0,
      capacidade_investimento_expansao: 1.0,
      prontidao_expansao: 1.0
    },
    nonScoringCategories: ['estrutura_rede'],
    questionCategories,
    scoreThresholds: [85, 70],
    finalRecommendations: [
      'Bora começar a vender hoje mesmo! Sua rede está pronta para uma expansão expressiva.',
      'Sua rede está no jogo! Com os ajustes certos, os resultados de expansão vão escalar rápido.',
      'Sua marca está iniciando a jornada de expansão — com orientação adequada, a evolução é rápida.'
    ],
    recommendationMaps: {
      specific: {
        exp_pron_h: {
          nao: 'Estruturar o processo de vendas é fundamental antes de escalar. COF, contrato e materiais de apresentação são essenciais.',
          parcial: 'Complete a estruturação do processo comercial para garantir mais conversões ao expandir.'
        },
        exp_pron_i: {
          nao: 'Invista em canais de geração de leads qualificados para sua franquia antes de iniciar a expansão.',
          raramente: 'Regularize a geração de leads com estratégias de marketing digital e presença em portais de franquias.'
        },
        exp_eq_j: {
          ainda_nao: 'Ter uma equipe comercial dedicada é fundamental para escalar as vendas de franquias. A Inova pode alocar um time para você.'
        }
      },
      general: {
        estrutura_rede: {
          low: 'Sua rede ainda está em fase inicial. Consolide sua operação antes de escalar a expansão.',
          medium: 'Sua rede tem uma base razoável. Com os ajustes certos, o potencial de crescimento é grande.',
          high: 'Rede bem estruturada! Você tem a base ideal para uma expansão expressiva.'
        },
        capacidade_investimento_expansao: {
          low: 'O investimento em marketing e expansão precisa ser ampliado para atrair franqueados qualificados.',
          medium: 'Sua capacidade de investimento é razoável. Com otimização, os resultados de captação podem melhorar significativamente.',
          high: 'Excelente capacidade de investimento! Você tem os recursos para uma campanha de expansão robusta.'
        },
        prontidao_expansao: {
          low: 'Ainda há pilares importantes a estruturar antes de iniciar a expansão. Nossa consultoria ajuda a acelerar esse processo.',
          medium: 'Você está no caminho certo para a expansão. Alguns ajustes estratégicos garantirão melhores resultados.',
          high: 'Sua rede está pronta para expandir! Você tem processo, leads, equipe e timing ideais para crescer com a Inova.'
        }
      }
    }
  }
};
